import React from 'react';
import { any, string } from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import 'react-dates/initialize';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConsentProvider, ConsentBanner } from 'react-hook-consent';
import difference from 'lodash/difference';
import mapValues from 'lodash/mapValues';
import moment from 'moment';
import './styles/cookieStyle.css';
import defaultConfig from './config/configDefault';
import appSettings from './config/settings';
import configureStore from './store';
import { RouteConfigurationProvider } from './context/routeConfigurationContext';
import { ConfigurationProvider } from './context/configurationContext';
import { NavigationInterceptorProvider } from './context/NavigationInterceptorContext';
import { SignInProvider } from './context/SignInContext';
import { mergeConfig } from './util/configHelpers';
import { IntlProvider } from './util/reactIntl';
import { IncludeScripts } from './util/includeScripts';
import routeConfiguration from './routing/routeConfiguration';
import Routes from './routing/Routes';
import localizationEL from 'moment/locale/el';
import englishMessages from './translations/en.json';
import greekMessages from './translations/el.json';
import germanMessages from './translations/de.json';
import arabicMessages from './translations/ar.json';
import {
  clearLanguageSwitchInProgress,
  getDefaultLocale,
  getLanguageSwitchStartedAt,
  handleDeferredLanguageSwitch,
  isLanguageSwitchInProgress,
  isArrayLength,
  LANGUAGE_SWITCH_EVENT_NAME,
} from './util/genericHelpers';
import { initMixpanel } from './util/mixpanel';
import { mergeTranslationMessages } from './util/translations';

// NEW: import the bridge component
import ConsentOpenBridge from './components/ConsentOpenBridge/ConsentOpenBridge';
import MixpanelIdentitySync from './components/MixpanelIdentitySync/MixpanelIdentitySync';
import { RippleSpinner } from './components';
import GlobalSignInDrawer from './components/Topbar/SignInDrawer/GlobalSignInDrawer';
import LoginQueryParamHandler from './components/Topbar/SignInDrawer/LoginQueryParamHandler';

const SUPPORTED_UI_LOCALES = ['en', 'el', 'de', 'ar'];
const LANGUAGE_SWITCH_MIN_VISIBLE_MS = 180;
const LANGUAGE_SWITCH_FALLBACK_TIMEOUT_MS = 10000;
const LANGUAGE_SWITCH_SPINNER_STYLE = {
  '--rippleSpinnerDuration': '3s',
  '--rippleSpinnerDelay': '1.5s',
};
const REMOTE_TRANSLATIONS_CACHE_PREFIX = 'app.remoteTranslations.';
const REMOTE_TRANSLATIONS_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const remoteTranslationsMemoryCache = new Map();

// ClientOnly component for client-side rendering
const ClientOnly = ({ children }) => {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? children : null;
};

// Translations for ConsentBanner
const translations = {
  necessaryCookies: { en: 'Necessary', de: 'Notwendig', el: 'Απαραίτητα', ar: 'ضروري' },
  preferencesCookies: { en: 'Preferences', de: 'Präferenzen', el: 'Προτιμήσεις', ar: 'التفضيلات' },
  statisticsCookies: { en: 'Statistics', de: 'Statistiken', el: 'Στατιστικά', ar: 'الإحصائيات' },
  analyticsCookies: { en: 'Analytics', de: 'Analyse', el: 'Αναλυτικά', ar: 'التحليلات' },
  marketingCookies: { en: 'Marketing', de: 'Marketing', el: 'Μάρκετινγκ', ar: 'التسويق' },
  Approve: { en: 'Approve', de: 'Zustimmen', el: 'Αποδοχή', ar: 'موافقة' },
  Decline: { en: 'Decline', de: 'Ablehnen', el: 'Απόρριψη', ar: 'رفض' },
  Settings: { en: 'Settings', de: 'Einstellungen', el: 'Ρυθμίσεις', ar: 'الإعدادات' },
  CookieMessage: {
    en: `We use cookies and similar technologies to provide, improve, and secure our services. Cookies are small text files stored on your device that help us understand how you use our platform. Cookie Duration: Persistent cookies remain on your device for up to 12 months from your last visit, unless you delete them earlier.`,
    de: `Wir verwenden Cookies und ähnliche Technologien, um unsere Dienste bereitzustellen, zu verbessern und zu schützen. Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden und uns helfen zu verstehen, wie Sie unsere Plattform nutzen.
Cookie-Dauer: Persistente Cookies bleiben bis zu 12 Monate nach Ihrem letzten Besuch auf Ihrem Gerät gespeichert, sofern Sie sie nicht früher löschen.`,
    el: `Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες για να παρέχουμε, να βελτιώνουμε και να ασφαλίζουμε τις υπηρεσίες μας. Τα cookies είναι μικρά αρχεία κειμένου που αποθηκεύονται στη συσκευή σας και μας βοηθούν να κατανοήσουμε πώς χρησιμοποιείτε την πλατφόρμα μας.
Διάρκεια cookies: Τα μόνιμα (persistent) cookies παραμένουν στη συσκευή σας έως και 12 μήνες από την τελευταία σας επίσκεψη, εκτός αν τα διαγράψετε νωρίτερα.`,
    ar: `نستخدم ملفات تعريف الارتباط والتقنيات المشابهة لتوفير خدماتنا وتحسينها وتأمينها. ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم تخزينها على جهازك وتساعدنا على فهم كيفية استخدامك لمنصتنا. مدة ملفات تعريف الارتباط: تبقى ملفات تعريف الارتباط المستمرة على جهازك لمدة تصل إلى 12 شهرًا من آخر زيارة لك، ما لم تحذفها في وقت سابق.`,
  },
  necessaryDescription: {
    en:
      'These cookies are essential for the platform to function properly. They enable core features like secure login, account authentication, session management, and access to protected areas of the website. Without these cookies, the services you request cannot be provided. Examples: Session cookies, authentication tokens, security cookies, load balancing cookies',
    de: `Diese Cookies sind für das ordnungsgemäße Funktionieren der Plattform unbedingt erforderlich. Sie ermöglichen grundlegende Funktionen wie sicheren Login, Konten-Authentifizierung, Sitzungsverwaltung und den Zugriff auf geschützte Bereiche der Website. Ohne diese Cookies können die von Ihnen angeforderten Dienste nicht bereitgestellt werden.
Beispiele: Sitzungs-Cookies, Authentifizierungs-Token, Sicherheits-Cookies, Load-Balancing-Cookies.`,
    el: `Αυτά τα cookies είναι απολύτως απαραίτητα για τη σωστή λειτουργία της πλατφόρμας. Επιτρέπουν βασικές λειτουργίες, όπως η ασφαλής σύνδεση, η ταυτοποίηση λογαριασμού, η διαχείριση συνεδρίας και η πρόσβαση σε προστατευμένες περιοχές του ιστότοπου. Χωρίς αυτά, δεν είναι δυνατή η παροχή των υπηρεσιών που ζητάτε.
Παραδείγματα: Cookies συνεδρίας, διακριτικά ταυτοποίησης (authentication tokens), cookies ασφαλείας, cookies εξισορρόπησης φόρτου (load balancing).`,
    ar: `هذه ملفات تعريف الارتباط ضرورية لعمل المنصة بشكل صحيح. تمكن الميزات الأساسية مثل تسجيل الدخول الآمن، والمصادقة على الحساب، وإدارة الجلسات، والوصول إلى المناطق المحمية من الموقع. بدون هذه الملفات، لا يمكن توفير الخدمات التي تطلبها. أمثلة: ملفات تعريف الارتباط للجلسة، رموز المصادقة، ملفات تعريف الارتباط الأمنية، ملفات تعريف الارتباط لموازنة التحميل`,
  },
  preferencesDescription: {
    en: `Preference cookies remember your choices and settings to provide a more personalised experience. They store information like your language preference, display settings, region, and other customisation options you've selected.Examples: Language preference, display mode (light/dark), timezone settings, accessibility preferences`,
    de: `Präferenz-Cookies speichern Ihre Einstellungen und Auswahl, um Ihnen ein persönlicheres Nutzungserlebnis zu bieten. Sie speichern Informationen wie Ihre bevorzugte Sprache, Anzeigeeinstellungen, Region und andere von Ihnen gewählte Anpassungen.
Beispiele: Sprachpräferenz, Anzeige-Modus (Hell/Dunkel), Zeitzonen-Einstellungen, Barrierefreiheits-Einstellungen.`,
    el: `Τα cookies προτιμήσεων αποθηκεύουν τις επιλογές και ρυθμίσεις σας, ώστε να σας προσφέρουμε μια πιο εξατομικευμένη εμπειρία. Αποθηκεύουν πληροφορίες όπως τη γλώσσα προτίμησής σας, τις ρυθμίσεις εμφάνισης, την περιοχή σας και άλλες επιλογές εξατομίκευσης που έχετε επιλέξει.
Παραδείγματα: Προτίμηση γλώσσας, λειτουργία εμφάνισης (light/dark mode), ρυθμίσεις ζώνης ώρας, ρυθμίσεις προσβασιμότητας.
`,
    ar: `تتذكر ملفات تعريف الارتباط للتفضيلات اختياراتك وإعداداتك لتوفير تجربة أكثر تخصيصًا. تخزن معلومات مثل تفضيل اللغة، وإعدادات العرض، والمنطقة، وخيارات التخصيص الأخرى التي اخترتها. أمثلة: تفضيل اللغة، وضع العرض (فاتح/داكن)، إعدادات المنطقة الزمنية، تفضيلات إمكانية الوصول`,
  },
  statisticsDescription: {
    en: `Statistics cookies help us understand how visitors interact with our platform by collecting and reporting anonymous usage information. This data helps us improve our services, identify technical issues, and optimise user experience. These cookies do not identify you personally. Examples: Page views, session duration, navigation paths, feature usage statistics`,
    de: `Statistik-Cookies helfen uns zu verstehen, wie Besucher mit unserer Plattform interagieren, indem sie anonyme Nutzungsinformationen sammeln und auswerten. Diese Daten unterstützen uns dabei, unsere Dienste zu verbessern, technische Probleme zu erkennen und die Nutzererfahrung zu optimieren. Diese Cookies identifizieren Sie nicht persönlich.
Beispiele: Seitenaufrufe, Sitzungsdauer, Navigationspfade, Nutzungsstatistiken von Funktionen.`,
    el: `Τα cookies στατιστικών μας βοηθούν να κατανοήσουμε πώς οι επισκέπτες αλληλεπιδρούν με την πλατφόρμα, συλλέγοντας και αναφέροντας ανώνυμες πληροφορίες χρήσης. Τα δεδομένα αυτά μας βοηθούν να βελτιώνουμε τις υπηρεσίες μας, να εντοπίζουμε τεχνικά προβλήματα και να βελτιστοποιούμε την εμπειρία χρήστη. Τα cookies αυτά δεν σας ταυτοποιούν προσωπικά.
Παραδείγματα: Προβολές σελίδων, διάρκεια συνεδρίας, διαδρομές πλοήγησης, στατιστικά χρήσης λειτουργιών.
`,
    ar: `تساعدنا ملفات تعريف الارتباط الإحصائية على فهم كيفية تفاعل الزوار مع منصتنا من خلال جمع معلومات الاستخدام المجهولة والإبلاغ عنها. تساعدنا هذه البيانات على تحسين خدماتنا، وتحديد المشكلات التقنية، وتحسين تجربة المستخدم. لا تحدد هذه الملفات هويتك شخصيًا. أمثلة: مشاهدات الصفحة، مدة الجلسة، مسارات التنقل، إحصائيات استخدام الميزات`,
  },
  analyticsDescription: {
    en: `Analytics cookies collect detailed information about how you use our platform, including which features you access, how long you spend on different sections, and your interaction patterns. We use this data to analyze user behavior, improve our services, and develop new features. Third-party services: We use Google Analytics to process this data. Google Analytics may set cookies to help analyse website traffic and usage patterns.`,
    de: `Analyse-Cookies erfassen detaillierte Informationen darüber, wie Sie unsere Plattform nutzen – zum Beispiel, welche Funktionen Sie aufrufen, wie lange Sie in bestimmten Bereichen bleiben und wie Sie mit verschiedenen Elementen interagieren. Wir nutzen diese Daten, um das Nutzerverhalten zu analysieren, unsere Dienste zu verbessern und neue Funktionen zu entwickeln.
Dienste von Drittanbietern: Wir verwenden Google Analytics zur Verarbeitung dieser Daten. Google Analytics kann Cookies setzen, um den Website-Traffic und Nutzungsmuster zu analysieren.
`,
    el: `Τα analytics cookies συλλέγουν πιο λεπτομερείς πληροφορίες για τον τρόπο με τον οποίο χρησιμοποιείτε την πλατφόρμα, όπως ποιες λειτουργίες χρησιμοποιείτε, πόση ώρα περνάτε σε διαφορετικές ενότητες και τα πρότυπα αλληλεπίδρασής σας. Χρησιμοποιούμε αυτά τα δεδομένα για να αναλύουμε τη συμπεριφορά των χρηστών, να βελτιώνουμε τις υπηρεσίες μας και να αναπτύσσουμε νέες δυνατότητες.
Υπηρεσίες τρίτων: Χρησιμοποιούμε το Google Analytics για την επεξεργασία αυτών των δεδομένων. Το Google Analytics μπορεί να τοποθετεί cookies για την ανάλυση της επισκεψιμότητας και των προτύπων χρήσης του ιστότοπου.`,
    ar: `تجمع ملفات تعريف الارتباط التحليلية معلومات مفصلة حول كيفية استخدامك لمنصتنا، بما في ذلك الميزات التي تصل إليها، والوقت الذي تقضيه في الأقسام المختلفة، وأنماط تفاعلك. نستخدم هذه البيانات لتحليل سلوك المستخدم، وتحسين خدماتنا، وتطوير ميزات جديدة. خدمات الطرف الثالث: نستخدم Google Analytics لمعالجة هذه البيانات. قد يضع Google Analytics ملفات تعريف الارتباط للمساعدة في تحليل حركة المرور على الموقع وأنماط الاستخدام.`,
  },
  marketingDescription: {
    en: `Marketing cookies track your activity across websites to deliver relevant advertisements and measure the effectiveness of our marketing campaigns. These cookies may be set by our advertising partners and can be used to build a profile of your interests. Examples: Ad targeting cookies, conversion tracking, retargeting pixels, social media integration cookies`,
    de: `Marketing-Cookies verfolgen Ihre Aktivitäten über verschiedene Websites hinweg, um Ihnen relevante Werbung anzuzeigen und die Wirksamkeit unserer Marketingkampagnen zu messen. Diese Cookies können von unseren Werbepartnern gesetzt werden und zur Erstellung eines Profils Ihrer Interessen verwendet werden.
Beispiele: Cookies für Werbe-Targeting, Conversion-Tracking, Retargeting-Pixel, Cookies für Social-Media-Integration.`,
    el: `Τα cookies μάρκετινγκ παρακολουθούν τη δραστηριότητά σας σε διάφορους ιστότοπους με σκοπό την εμφάνιση σχετικών διαφημίσεων και τη μέτρηση της αποτελεσματικότητας των διαφημιστικών μας καμπανιών. Αυτά τα cookies μπορεί να τοποθετούνται από διαφημιστικούς συνεργάτες μας και να χρησιμοποιούνται για τη δημιουργία προφίλ των ενδιαφερόντων σας.
Παραδείγματα: Cookies στόχευσης διαφημίσεων, cookies παρακολούθησης μετατροπών (conversion tracking), pixels επαναστόχευσης (retargeting pixels), cookies ενσωμάτωσης κοινωνικών δικτύων.`,
    ar: `تتعقب ملفات تعريف الارتباط التسويقية نشاطك عبر المواقع الإلكترونية لتقديم إعلانات ذات صلة وقياس فعالية حملاتنا التسويقية. قد يتم تعيين هذه الملفات من قبل شركائنا الإعلانيين ويمكن استخدامها لبناء ملف تعريف لاهتماماتك. أمثلة: ملفات تعريف الارتباط المستهدفة للإعلانات، تتبع التحويلات، بكسل إعادة الاستهداف، ملفات تعريف الارتباط لدمج وسائل التواصل الاجتماعي`,
  },
  ModalContent: {
    title: {
      en: 'Consent settings',
      de: 'Einstellungen zur Einwilligung',
      el: 'Ρυθμίσεις συγκατάθεσης',
      ar: 'إعدادات الموافقة',
    },
    description: {
      en: `We use cookies and similar technologies to provide, improve, and secure our services. Cookies are small text files stored on your device that help us understand how you use our platform.\nCookie Duration: Persistent cookies remain on your device for up to 12 months from your last visit, unless you delete them earlier.`,
      de: `Wir verwenden Cookies und ähnliche Technologien, um unsere Dienste bereitzustellen, zu verbessern und zu schützen. Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden und uns helfen zu verstehen, wie Sie unsere Plattform nutzen.
Cookie-Dauer: Persistente Cookies bleiben bis zu 12 Monate nach Ihrem letzten Besuch auf Ihrem Gerät gespeichert, sofern Sie sie nicht früher löschen.`,
      el: `Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες για να παρέχουμε, να βελτιώνουμε και να ασφαλίζουμε τις υπηρεσίες μας. Τα cookies είναι μικρά αρχεία κειμένου που αποθηκεύονται στη συσκευή σας και μας βοηθούν να κατανοήσουμε πώς χρησιμοποιείτε την πλατφόρμα μας.
Διάρκεια cookies: Τα μόνιμα (persistent) cookies παραμένουν στη συσκευή σας έως και 12 μήνες από την τελευταία σας επίσκεψη, εκτός αν τα διαγράψετε νωρίτερα.`,
      ar: `نستخدم ملفات تعريف الارتباط والتقنيات المشابهة لتوفير خدماتنا وتحسينها وتأمينها. ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم تخزينها على جهازك وتساعدنا على فهم كيفية استخدامك لمنصتنا. مدة ملفات تعريف الارتباط: تبقى ملفات تعريف الارتباط المستمرة على جهازك لمدة تصل إلى 12 شهرًا من آخر زيارة لك، ما لم تحذفها في وقت سابق.`,
    },
    decline: { label: { en: 'Decline', de: 'Ablehnen', el: 'Απόρριψη', ar: 'رفض' } },
    approve: {
      label: {
        en: 'Approve selection',
        de: 'Auswahl genehmigen',
        el: 'Αποδοχή επιλεγμένων',
        ar: 'الموافقة على التحديد',
      },
    },
    approveAll: {
      label: {
        en: 'Approve all',
        de: 'Alles genehmigen',
        el: 'Αποδοχή όλων',
        ar: 'الموافقة على الكل',
      },
    },
  },
};

// Translation helper
const t = (obj, locale) => obj?.[locale] ?? obj?.en ?? '';

// Set default locale to English if not specified
const defaultLocale = getDefaultLocale() || 'en';

// Consent options based on locale
const consentOptionsFor = locale => ({
  services: [
    {
      id: 'necessary',
      name: t(translations.necessaryCookies, locale),
      description: t(translations.necessaryDescription, locale),
      mandatory: true,
      cookies: [{ pattern: 'necessary_*' }],
    },
    {
      id: 'preferences',
      name: t(translations.preferencesCookies, locale),
      description: t(translations.preferencesDescription, locale),
      cookies: [{ pattern: 'pref_*' }],
    },
    {
      id: 'statistics',
      name: t(translations.statisticsCookies, locale),
      description: t(translations.statisticsDescription, locale),
      cookies: [{ pattern: 'stats_*' }],
    },
    {
      id: 'analytics',
      name: t(translations.analyticsCookies, locale),
      description: t(translations.analyticsDescription, locale),
      cookies: [{ pattern: 'analytics_*' }],
      scripts: [{ id: 'analytics-script', src: 'https://example.com/analytics.js' }],
    },
    {
      id: 'marketing',
      name: t(translations.marketingCookies, locale),
      description: t(translations.marketingDescription, locale),
      cookies: [{ pattern: 'marketing_*' }],
      scripts: [{ id: 'marketing-script', src: 'https://example.com/marketing.js' }],
    },
  ],
  theme: 'dark',
});

// Load translations based on default locale
const translationMessages = {
  en: englishMessages,
  el: greekMessages,
  de: germanMessages,
  ar: arabicMessages,
};

const addMissingTranslations = (sourceTranslations, targetTranslations) => {
  const sourceKeys = Object.keys(sourceTranslations);
  const targetKeys = Object.keys(targetTranslations || {});
  const missingKeys = difference(sourceKeys, targetKeys);
  return missingKeys.reduce(
    (acc, key) => ({ ...acc, [key]: sourceTranslations[key] }),
    targetTranslations || {}
  );
};

const messages = addMissingTranslations(englishMessages, translationMessages[defaultLocale] || {});
const isTestEnv = process.env.NODE_ENV === 'test';
const localeMessages = isTestEnv ? mapValues(messages, (val, key) => key) : messages;

const setupLocale = appConfig => {
  const locale = isTestEnv ? 'en' : appConfig.localization?.locale || defaultLocale;
  moment.locale(locale);
};

// Configurations wrapper
const Configurations = ({ appConfig, children }) => {
  const routeConfig = routeConfiguration(appConfig.layout);
  setupLocale(appConfig);
  return (
    <ConfigurationProvider value={appConfig}>
      <RouteConfigurationProvider value={routeConfig}>{children}</RouteConfigurationProvider>
    </ConfigurationProvider>
  );
};

Configurations.propTypes = { appConfig: any.isRequired, children: any.isRequired };

// Helper: map FTW locale → Strapi locale if they differ
const mapLocaleForStrapi = ftwLocale => {
  // Adjust this if your Strapi locales differ
  if (ftwLocale === 'el') return 'el-GR';
  if (ftwLocale === 'ar') return 'ar';
  return ftwLocale;
};

const getRemoteTranslationsCacheKey = locale => `${REMOTE_TRANSLATIONS_CACHE_PREFIX}${locale}`;

const readCachedRemoteTranslations = locale => {
  if (!locale) return null;

  if (remoteTranslationsMemoryCache.has(locale)) {
    return remoteTranslationsMemoryCache.get(locale);
  }

  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(getRemoteTranslationsCacheKey(locale));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt || 0);
    const messages = parsed?.messages;

    if (!cachedAt || Date.now() - cachedAt > REMOTE_TRANSLATIONS_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(getRemoteTranslationsCacheKey(locale));
      return null;
    }

    if (!messages || typeof messages !== 'object') {
      return null;
    }

    remoteTranslationsMemoryCache.set(locale, messages);
    return messages;
  } catch (e) {
    return null;
  }
};

const writeCachedRemoteTranslations = (locale, messages) => {
  if (!locale || !messages || typeof messages !== 'object') return;

  remoteTranslationsMemoryCache.set(locale, messages);

  if (typeof window === 'undefined') return;

  try {
    const payload = JSON.stringify({ cachedAt: Date.now(), messages });
    window.sessionStorage.setItem(getRemoteTranslationsCacheKey(locale), payload);
  } catch (e) {
    // Ignore quota/storage errors and keep in-memory cache only.
  }
};

const fetchHostedTranslationsForLocale = async ({ strapiBaseUrl, locale }) => {
  const strapiLocale = mapLocaleForStrapi(locale);

  const query = new URLSearchParams({
    locale: strapiLocale,
    populate: '*',
  }).toString();

  const url = `${strapiBaseUrl.replace(/\/$/, '')}/api/translations?${query}`;
  const response = await fetch(url, { method: 'GET' });

  if (!response.ok) {
    throw new Error(`Strapi translations fetch failed with status ${response.status}`);
  }

  const json = await response.json();
  const dataArray = Array.isArray(json?.data) ? json.data : [];
  const exactMatch = dataArray.find(entry => entry?.attributes?.type === locale);
  const fallbackMatch = dataArray.find(entry => entry?.attributes?.json);
  const resolved = exactMatch?.attributes?.json || fallbackMatch?.attributes?.json || null;

  return resolved && typeof resolved === 'object' ? resolved : null;
};

// NEW: Strapi path for annual coupons (single type)
const STRAPI_ANNUAL_COUPONS_PATH =
  process.env.REACT_APP_STRAPI_ANNUAL_COUPONS_PATH || '/api/stripe-annual-coupon';

export const ClientApp = ({ store, hostedTranslations = {}, hostedConfig = {} }) => {
  // Make appConfig stateful so we can merge coupons into it later
  const [appConfig, setAppConfig] = React.useState(() => mergeConfig(hostedConfig, defaultConfig));

  React.useEffect(() => {
    initMixpanel();
  }, []);

  // Apply marketplace colors
  const elem = window.document.documentElement;
  if (appConfig.branding?.marketplaceColor) {
    elem.style.setProperty('--marketplaceColor', appConfig.branding.marketplaceColor);
    elem.style.setProperty('--marketplaceColorDark', appConfig.branding.marketplaceColorDark);
    elem.style.setProperty('--marketplaceColorLight', appConfig.branding.marketplaceColorLight);
  }

  const logLoadDataCalls = appSettings?.env !== 'test';
  handleDeferredLanguageSwitch();

  const activeLocale = isTestEnv ? 'en' : getDefaultLocale();
  const hasHostedRemote = hostedTranslations && Object.keys(hostedTranslations).length > 0;
  const cachedRemoteTranslations =
    !hasHostedRemote && typeof window !== 'undefined'
      ? readCachedRemoteTranslations(activeLocale)
      : null;
  const hasInitialRemote = hasHostedRemote || !!cachedRemoteTranslations;

  const strapiBaseUrl = appConfig?.strapiBaseUrl || process.env.REACT_APP_STRAPI_BASE_URL || '';

  // Start with hosted translations if any were passed in (SSR etc.) or cached locale messages.
  const [remoteTranslations, setRemoteTranslations] = React.useState(() =>
    hasHostedRemote ? hostedTranslations : cachedRemoteTranslations || {}
  );

  // We consider translations "ready" if:
  // - we already have hostedTranslations, OR
  // - there is no Strapi base URL configured (so we just use local JSON), OR
  // - we are in test env (don’t block tests on fetch)
  const [translationsReady, setTranslationsReady] = React.useState(
    hasInitialRemote || !strapiBaseUrl || isTestEnv
  );
  const [isSwitchingLanguage, setIsSwitchingLanguage] = React.useState(() =>
    isLanguageSwitchInProgress()
  );

  // Optional local state if you ever want to inspect coupons separately
  const [annualCoupons, setAnnualCoupons] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;

    // If already ready, or no Strapi URL, or test env → don’t fetch
    if (translationsReady || !strapiBaseUrl || isTestEnv) {
      return undefined;
    }

    const loadHostedTranslations = async () => {
      try {
        const cached = readCachedRemoteTranslations(activeLocale);
        if (cached) {
          if (!cancelled) {
            setRemoteTranslations(cached);
            setTranslationsReady(true);
          }
          return;
        }

        const hosted = await fetchHostedTranslationsForLocale({
          strapiBaseUrl,
          locale: activeLocale,
        });

        if (hosted && !cancelled) {
          setRemoteTranslations(hosted);
          writeCachedRemoteTranslations(activeLocale, hosted);
        } else if (!cancelled) {
          console.warn(
            '[Translations] No usable translation keys found in Strapi. Check your content type fields (messages / en / el / de / ar / defaultMessages).'
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            '[Translations] Failed to load translations from Strapi, falling back to local JSON',
            err
          );
        }
      } finally {
        if (!cancelled) {
          // Whether success or failure, we can now render the app
          setTranslationsReady(true);
        }
      }
    };

    loadHostedTranslations();

    return () => {
      cancelled = true;
    };
  }, [activeLocale, strapiBaseUrl, translationsReady]);

  React.useEffect(() => {
    if (!translationsReady || !strapiBaseUrl || isTestEnv) return undefined;

    let cancelled = false;
    const localesToPrefetch = SUPPORTED_UI_LOCALES.filter(locale => locale !== activeLocale);

    const prefetch = async locale => {
      if (readCachedRemoteTranslations(locale)) return;

      try {
        const hosted = await fetchHostedTranslationsForLocale({
          strapiBaseUrl,
          locale,
        });

        if (!cancelled && hosted) {
          writeCachedRemoteTranslations(locale, hosted);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(`[Translations] Failed to prefetch locale "${locale}"`, err);
        }
      }
    };

    localesToPrefetch.forEach(locale => {
      prefetch(locale);
    });

    return () => {
      cancelled = true;
    };
  }, [activeLocale, strapiBaseUrl, translationsReady]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onLanguageSwitchStart = () => {
      setIsSwitchingLanguage(true);
    };

    window.addEventListener(LANGUAGE_SWITCH_EVENT_NAME, onLanguageSwitchStart);
    return () => {
      window.removeEventListener(LANGUAGE_SWITCH_EVENT_NAME, onLanguageSwitchStart);
    };
  }, []);

  React.useEffect(() => {
    if (!isSwitchingLanguage || !translationsReady) return undefined;

    if (typeof window === 'undefined') return undefined;

    const startedAt = getLanguageSwitchStartedAt();
    const elapsed = startedAt ? Math.max(0, Date.now() - startedAt) : 0;
    const delay = Math.max(0, LANGUAGE_SWITCH_MIN_VISIBLE_MS - elapsed);

    let timeoutId = null;
    const rafId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        clearLanguageSwitchInProgress();
        setIsSwitchingLanguage(false);
      }, delay);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isSwitchingLanguage, translationsReady]);

  React.useEffect(() => {
    if (!isSwitchingLanguage || typeof window === 'undefined') return undefined;

    const timeoutId = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.error('[i18n] Language switch overlay timeout reached, forcing reset.');
      clearLanguageSwitchInProgress();
      setIsSwitchingLanguage(false);
    }, LANGUAGE_SWITCH_FALLBACK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSwitchingLanguage]);

  const languageSwitchOverlay = isSwitchingLanguage ? (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483646,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 250, 246, 0.94)',
      }}
    >
      <RippleSpinner style={LANGUAGE_SWITCH_SPINNER_STYLE} />
    </div>
  ) : null;

  // NEW: After translations are ready, fetch annual coupons from Strapi and merge into appConfig
  React.useEffect(() => {
    if (!translationsReady || !strapiBaseUrl || isTestEnv) return;

    let cancelled = false;

    const loadAnnualCoupons = async () => {
      const base = strapiBaseUrl.replace(/\/$/, '');
      const url = `${base}${STRAPI_ANNUAL_COUPONS_PATH}?populate=*`;

      try {
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) {
          throw new Error(`Strapi annual coupons fetch failed with status ${res.status}`);
        }

        const json = await res.json();

        // Assuming single-type structure: { data: { id, attributes: { coupons: [...] } } }
        const attributes = json?.data?.attributes || {};
        const couponsArray = isArrayLength(attributes.couponCodes)
          ? attributes.couponCodes.flatMap(c => c?.code)
          : [];

        if (!cancelled) {
          setAnnualCoupons(couponsArray);

          // Merge into appConfig so it's available via ConfigurationProvider
          setAppConfig(prev => ({
            ...prev,
            stripeAnnualCoupons: couponsArray,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[Strapi] Failed to load annual coupons', err);
        }
      }
    };

    loadAnnualCoupons();

    return () => {
      cancelled = true;
    };
  }, [translationsReady, strapiBaseUrl]);

  // 🔒 Block the app UI until translations are ready.
  if (!translationsReady) {
    // You can swap this for your own branded loader / splash screen
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 250, 246, 0.94)',
        }}
      >
        <RippleSpinner style={LANGUAGE_SWITCH_SPINNER_STYLE} />
      </div>
    );
  }

  // Once translations are ready, merge local + remote translations
  const mergedMessages = mergeTranslationMessages({
    localMessages: localeMessages,
    remoteMessages: remoteTranslations,
  });

  return (
    <Configurations appConfig={appConfig}>
      <IntlProvider locale={activeLocale} messages={mergedMessages} textComponent="span">
        <Provider store={store}>
          <MixpanelIdentitySync />
          <ConsentProvider options={consentOptionsFor(activeLocale)}>
            <HelmetProvider>
              <IncludeScripts config={appConfig} />
              <BrowserRouter>
                <SignInProvider>
                  <NavigationInterceptorProvider>
                    <Routes
                      routes={routeConfiguration(appConfig.layout)}
                      logLoadDataCalls={logLoadDataCalls}
                    />
                  </NavigationInterceptorProvider>
                  <LoginQueryParamHandler />
                  <GlobalSignInDrawer />
                  <ClientOnly>
                    <ConsentBanner
                      settings={{
                        hidden: false,
                        label: t(translations.Settings, activeLocale),
                        modal: {
                          title: t(translations.ModalContent.title, activeLocale),
                          description: t(translations.ModalContent.description, activeLocale),
                          decline: {
                            label: t(translations.ModalContent.decline.label, activeLocale),
                          },
                          approve: {
                            label: t(translations.ModalContent.approve.label, activeLocale),
                          },
                          approveAll: {
                            label: t(translations.ModalContent.approveAll.label, activeLocale),
                          },
                        },
                      }}
                      decline={{ show: true, label: t(translations.Decline, activeLocale) }}
                      approve={{ label: t(translations.Approve, activeLocale) }}
                      onClick={e => {
                        e.stopPropagation();
                        // eslint-disable-next-line no-console
                        console.log('ConsentBanner clicked');
                      }}
                    >
                      <div>{t(translations.CookieMessage, activeLocale)}</div>
                    </ConsentBanner>

                    {/* IMPORTANT: exposes window.__openConsent() so footer can open the SAME Settings modal */}
                    <ConsentOpenBridge />
                  </ClientOnly>
                </SignInProvider>
              </BrowserRouter>
              {languageSwitchOverlay}
            </HelmetProvider>
          </ConsentProvider>
        </Provider>
      </IntlProvider>
    </Configurations>
  );
};

ClientApp.propTypes = { store: any.isRequired };

export const ServerApp = ({
  url,
  context,
  helmetContext,
  store,
  hostedTranslations = {},
  hostedConfig = {},
}) => {
  const appConfig = mergeConfig(hostedConfig, defaultConfig);
  HelmetProvider.canUseDOM = false;

  return (
    <Configurations appConfig={appConfig}>
      <IntlProvider
        locale={defaultLocale}
        // On the server we rely on hostedTranslations passed into renderApp.
        // If nothing is passed, only local JSON is used.
        messages={{ ...localeMessages, ...hostedTranslations }}
        textComponent="span"
      >
        <Provider store={store}>
          <HelmetProvider context={helmetContext}>
            <IncludeScripts config={appConfig} />
            <StaticRouter location={url} context={context}>
              <NavigationInterceptorProvider>
                <Routes />
              </NavigationInterceptorProvider>
            </StaticRouter>
          </HelmetProvider>
        </Provider>
      </IntlProvider>
    </Configurations>
  );
};

ServerApp.propTypes = {
  url: string.isRequired,
  context: any.isRequired,
  helmetContext: any.isRequired,
  store: any.isRequired,
};

export const renderApp = (
  url,
  serverContext,
  preloadedState,
  hostedTranslations,
  collectChunks
) => {
  const store = configureStore(preloadedState);
  const helmetContext = {};
  const WithChunks = collectChunks(
    <ServerApp
      url={url}
      context={serverContext}
      helmetContext={helmetContext}
      store={store}
      hostedTranslations={hostedTranslations}
    />
  );
  const body = ReactDOMServer.renderToString(WithChunks);
  const { helmet: head } = helmetContext;
  return { head, body };
};
